import * as d3 from 'd3'

import { VIEW_MODES } from '@aytm/app/stats/page/question-views/hm/components/chart/lib/constants'
import { createSvgRoot } from '@aytm/app/stats/page/question-views/hm/components/chart/lib/render/svg-root'
import {
  getSvgDefs,
  createBlurFilter,
  createSpotlightMask
} from '@aytm/app/stats/page/question-views/hm/components/chart/lib/render/svg-defs'
import {
  createDomain,
  getUniqueContourPoints
} from '@aytm/app/stats/page/question-views/hm/components/chart/lib/render/utils'

// 30 is max possible value for stdDeviation or it will be too blurred
const DEFAULT_STD_DEVIATION_VALUE = 30

const createLayers = answer => {
  if (!answer?.heatmap?.levels || !answer?.heatmap?.contours) {
    return []
  }

  const contours = answer.heatmap.contours || []
  const levels = answer.heatmap.levels
  const levelLayers = []
  const levelsMaxNumber = Math.min(3, levels.length)
  const levelsCounter = Math.round(levels.length / levelsMaxNumber)

  for (let i = 0; i <= levelsMaxNumber; i++) {
    if (i === 0) {
      levelLayers[i] = [...contours]
    } else {
      const levelsForLayer = levels.slice((i - 1) * levelsCounter, i * levelsCounter)
      levelLayers[i] = contours.filter(contour => levelsForLayer.includes(contour.level))
    }
  }

  return levelLayers.filter(layer => layer.length)
}

export const drawSpotlight = (container, options) => {
  const { id, answer, image, scale, width, height } = options
  const imageHeight = image?.original?.height
  const viewMode = VIEW_MODES.SPOTLIGHT
  const levelLayers = createLayers(answer)

  if (!answer?.heatmap || !levelLayers?.length) return null

  const svg = createSvgRoot(container, { id, viewMode, width, height, scale })
  const defs = svg.append('defs')
  defs.html(
    getSvgDefs(id, viewMode, {
      imagePath: image.original.path,
      imageWidth: width,
      imageHeight: height
    })
  )

  svg
    .append('use')
    .attr('href', `#image-source-${id}`)
    .attr('height', height)
    .attr('width', width)
    .attr('x', 0)
    .attr('y', 0)
    .attr('id', `heatmap-orange-overlay-${id}`)
    .attr('filter', `url(#orange-color-filter-${id})`)

  const group = svg.append('g').attr('id', `svg-spotlight-${id}-group`)

  const layers = [...levelLayers].reverse()
  const opacityValues = [0.2, 1]
  const domain = createDomain(layers.length, opacityValues.length)
  const getOpacity = d3.scaleLinear().domain(domain).range(opacityValues)

  // First pass: create masks with polygons and masked groups — add to DOM
  layers.forEach((layer, index) => {
    if (!layer.length) return

    const maskGroup = createSpotlightMask(defs, id, index, width, height)

    layer.forEach(item => {
      const points = getUniqueContourPoints(item.contour, imageHeight)

      maskGroup.append('polygon').attr('points', points.map(p => p.join(',')).join(' '))
    })

    group
      .append('g')
      .attr('id', `svg-spotlight-${id}-masked-group-${index}`)
      .attr('mask', `url(#svg-spotlight-${id}-mask-${index})`)
      .style('opacity', getOpacity(index))
      .append('use')
      .attr('id', `spotlight-base-${id}-${index}`)
      .attr('class', 'svg-spotlight-base')
      .attr('height', `${height}px`)
      .attr('width', `${width}px`)
      .attr('href', `#image-source-${id}`)
      .attr('filter', `url(#spotlight-duotone-blue-${id})`)
  })

  // Second pass: elements are in DOM — read real sizes, calculate dynamic stdDeviation
  layers.forEach((layer, index) => {
    if (!layer.length) return

    const maskGroupEl = document.querySelector(`#svg-spotlight-${id}-mask-group-${index}`)
    const sizes = maskGroupEl?.getBoundingClientRect()

    let stdDeviation = DEFAULT_STD_DEVIATION_VALUE

    if (sizes?.width && sizes?.height) {
      const maxStdDeviation = (Math.min(sizes.width, sizes.height) - 40) / 2
      stdDeviation = maxStdDeviation <= 0 ? 1 : Math.min(DEFAULT_STD_DEVIATION_VALUE, maxStdDeviation)
    }

    createBlurFilter(defs, id, index, stdDeviation)
  })

  return null
}
